import { i18n } from '../i18n.js';
import { MoonfallCloud } from '../convex-client.js';
import { MoonfallAnalytics } from '../analytics/posthog-init.js';
import { getCloudAchievementBannerConsumer } from '../app-services.js';
import { MoonfallVoucherLink } from '../voucher-link.js';
import { ArrivalCeremony } from './arrival-ceremony.js';
import { errorCode, playerFacingError } from './player-error.js';
import { paintStardustChip } from './stardust-chip.js';
import { StarterReward } from './starter-reward.js';
import { Confirm } from './confirm-dialog.js';
import { giftReviewDetail, readGiftPreview } from './gift-review.js';
import {
  formatVoucherCodeForDisplay,
  isPlausibleVoucherCode,
  normalizeVoucherCode,
} from './voucher-code.js';

/**
 * VoucherRedeem — the ONE place a code is typed, and the shared claim path.
 *
 * The dialog opens from the username menu ("Redeem a code"). It is a
 * Confirm-class overlay: it claims no lobby surface, so it can open over the
 * Moon Store, and it renders into its final layout on first paint — the error
 * line reserves its own box so a refusal never moves the buttons.
 *
 * `redeemCode()` is the claim itself, used by this dialog AND by the arrival
 * ticket a voucher link raises: one mutation, one ceremony, one set of side
 * effects, so the two doors cannot drift apart.
 *
 * The server answers a refusal as a VALUE (`{ redeemed: false, code }`) — a
 * throw would roll back its own attempt counter — and auth/ban refusals as a
 * throw; both shapes go through the same copy registry, and a thrown message
 * is never shown.
 */
let VoucherRedeem = {};

VoucherRedeem = (function () {
  const OVERLAY_ID = 'voucher-redeem-overlay';
  const MODAL_SELECTOR = '[role="dialog"][aria-modal="true"], [role="alertdialog"][aria-modal="true"]';
  /* Refusals that are about THIS moment, not about the code: the code stays
     pending so a link's ticket can offer it again. Everything else is final. */
  const TRANSIENT_CODES = new Set(['voucher_too_many_attempts', 'voucher_unavailable', 'authentication_required', 'gift_preview_expired', 'upgrade_processing', 'gift_processing', 'gift_game_in_progress', 'gift_would_shorten_paid_service']);

  function uiText(key, fallback, replacements = {}) {
    const translated = i18n?.t?.(key, replacements);
    const template = translated && translated !== key ? translated : fallback;
    return Object.entries(replacements || {}).reduce(
      (result, [name, value]) => String(result)
        .split(`{{${name}}}`).join(String(value))
        .split(`{${name}}`).join(String(value)),
      String(template),
    );
  }

  function isOpen() {
    return Boolean(document.getElementById(OVERLAY_ID));
  }

  function anotherModalOwnsInput() {
    return Array.from(document.querySelectorAll(MODAL_SELECTOR)).some((element) => {
      if (element.id === OVERLAY_ID || element.closest(`#${OVERLAY_ID}`)) return false;
      if (element.id === 'store-modal' || element.closest('#store-modal')) return false;
      if (element.getAttribute('aria-hidden') === 'true' || /** @type {HTMLElement} */ (element).hidden) return false;
      const style = window.getComputedStyle?.(element);
      return !style || (style.display !== 'none' && style.visibility !== 'hidden');
    });
  }

  /* ---------------------------------------------------------- the claim */

  async function readMoonlightPercent() {
    try {
      const status = await MoonfallCloud?.users?.getMoonlightStatus?.();
      const percent = Number(status?.percent);
      return status?.state === 'known' && Number.isFinite(percent) ? percent : null;
    } catch (_) {
      return null;
    }
  }

  async function repaintStardustChip() {
    try {
      const profile = await MoonfallCloud?.users?.getProfile?.();
      const balance = profile?.stardust;
      /* Through the one chip painter: its format is the bare number (the
         Stardust glyph is CSS), and a second writer with its own format is how
         a chip ends up wearing two marks. */
      paintStardustChip(document.getElementById('user-stardust'), balance);
    } catch (_) { /* the grant is durable; the chip re-reads on the next surface */ }
  }

  function dispatch(name) {
    try { window.dispatchEvent(new CustomEvent(name)); } catch (_) { /* presentation only */ }
  }

  function capture(event, props) {
    try { MoonfallAnalytics?.capture?.(event, props); } catch (_) { /* inert without consent */ }
  }

  function grantKinds(granted) {
    return Array.isArray(granted) ? granted.map((grant) => grant?.kind).filter(Boolean) : [];
  }

  /**
   * Redeem a code and celebrate what it delivered.
   *
   * @param {string} code
   * @param {{source?: string, shouldContinue?: () => boolean, beforeReward?: () => void}} options
   * @returns {Promise<{ ok: true, result: object } | { ok: false, code: string, message: string, final: boolean }>}
   */
  async function redeemCode(code, { source = 'menu', shouldContinue = () => true, beforeReward = null } = {}) {
    const normalized = normalizeVoucherCode(code);
    if (!isPlausibleVoucherCode(normalized)) {
      return {
        ok: false,
        code: 'voucher_invalid',
        final: true,
        message: uiText('voucher.error.shape', 'Enter the code exactly as it was given to you.'),
      };
    }
    /* One answer for both refusal shapes. A FINAL refusal — including a ban,
       which arrives as a throw — drops the pending link code so the lobby
       never re-raises a ticket that can only be refused again. */
    /** @returns {{ ok: false, code: string, final: boolean, message: string }} */
    const refusal = (subject, refused) => {
      capture('voucher_redeem_refused', { code: refused, source });
      const final = !TRANSIENT_CODES.has(refused) && refused !== 'network';
      if (final) MoonfallVoucherLink?.clearPendingCode?.(code);
      return {
        ok: /** @type {false} */ (false),
        code: refused,
        final,
        message: playerFacingError(subject, uiText('voucher.error.generic', 'Could not redeem this code. Please try again.')),
      };
    };
    let result;
    try {
      // A weekly boundary can pass while the player reads. Refresh the facts
      // and ask again; the mutation also checks the reviewed reset atomically.
      for (let attempt = 0; attempt < 3; attempt += 1) {
        const preview = await MoonfallCloud.vouchers.previewRedemption(normalized);
        if (!shouldContinue()) return { ok: false, code: 'cancelled', message: '', final: false };
        const isPass = preview?.grants?.some((grant) => grant?.kind === 'complimentary_pass');
        let gift = null;
        if (isPass) {
          gift = readGiftPreview(preview.giftPreview);
          if (!gift) continue;
          const accepted = await Confirm.ask(uiText('voucher.giftReviewTitle', 'Review your gift'), {
            detail: giftReviewDetail(gift, uiText, i18n?.getLanguage?.() || 'en'),
            confirmLabel: uiText('voucher.giftReviewGo', 'Claim this gift'),
            cancelLabel: uiText('common.cancel', 'Cancel'),
          });
          if (!accepted || !shouldContinue()) return { ok: false, code: 'cancelled', message: '', final: false };
          if (gift.reviewResetAt != null && Date.now() >= gift.reviewResetAt) continue;
        }
        result = gift
          ? await MoonfallCloud.vouchers.redeem(normalized, gift.reviewResetAt == null ? {} : { expectedResetAt: gift.reviewResetAt })
          : await MoonfallCloud.vouchers.redeem(normalized);
        if (result?.code !== 'gift_preview_expired') break;
      }
    } catch (error) {
      return refusal(error, errorCode(error) || 'network');
    }
    if (!result || result.redeemed !== true) {
      return refusal(result, errorCode(result) || 'voucher_unavailable');
    }

    MoonfallVoucherLink?.clearPendingCode?.(code);
    capture('voucher_redeemed', { kind: result.kind, grantKinds: grantKinds(result.granted), source });

    const kinds = new Set(grantKinds(result.granted));
    const touchesMoonlight = kinds.has('moonlight') || kinds.has('complimentary_pass');
    if (touchesMoonlight) dispatch('moonfall:moonlight-changed');
    const moonlightPercent = touchesMoonlight ? await readMoonlightPercent() : null;

    const onClose = () => {
      void repaintStardustChip();
      if (result.cardbackUnlocked) {
        try { void getCloudAchievementBannerConsumer()?.(); } catch (_) { /* the banner drains on the next launch */ }
      }
      if (kinds.has('complimentary_pass')) dispatch('moonfall:subscription-changed');
    };
    // Release the typed dialog's isolation before the ceremony captures it.
    // Otherwise its dismissal would restore the stale inert=true snapshot.
    beforeReward?.();
    const shown = StarterReward?.show?.({
      kind: 'voucher',
      grants: result.granted,
      moonlightPercent,
      onClose,
    });
    if (!shown) onClose();
    return { ok: true, result };
  }

  /* --------------------------------------------------------- the dialog */

  function build({ code }) {
    const overlay = document.createElement('div');
    overlay.id = OVERLAY_ID;
    overlay.className = 'voucher-redeem';

    const panel = document.createElement('section');
    panel.id = 'voucher-redeem-panel';
    panel.className = 'voucher-redeem-panel';
    panel.setAttribute('role', 'dialog');
    panel.setAttribute('aria-modal', 'true');
    panel.setAttribute('aria-labelledby', 'voucher-redeem-title');
    panel.setAttribute('aria-describedby', 'voucher-redeem-help');

    const eyebrow = document.createElement('p');
    eyebrow.className = 'voucher-redeem-eyebrow';
    eyebrow.textContent = uiText('voucher.eyebrow', 'PROMO CODE');

    const title = document.createElement('h2');
    title.id = 'voucher-redeem-title';
    title.className = 'voucher-redeem-title';
    title.textContent = uiText('voucher.title', 'Redeem a code');

    const label = document.createElement('label');
    label.className = 'voucher-redeem-label';
    label.setAttribute('for', 'voucher-redeem-input');
    label.textContent = uiText('voucher.inputLabel', 'Your code');

    const input = document.createElement('input');
    input.id = 'voucher-redeem-input';
    input.className = 'voucher-redeem-input';
    input.type = 'text';
    input.autocomplete = 'off';
    input.spellcheck = false;
    input.setAttribute('autocapitalize', 'characters');
    input.setAttribute('inputmode', 'text');
    input.setAttribute('enterkeyhint', 'go');
    input.maxLength = 29;
    /* A format mask, not copy: the same in every language. */
    input.placeholder = 'XXXX-XXXX-XXXX';
    input.setAttribute('aria-describedby', 'voucher-redeem-help voucher-redeem-error');
    if (code) input.value = formatVoucherCodeForDisplay(code);

    const help = document.createElement('p');
    help.id = 'voucher-redeem-help';
    help.className = 'voucher-redeem-help';
    help.textContent = uiText('voucher.help', 'Enter the code exactly as it was shared with you. Dashes are optional.');

    /* Always present, so a refusal never moves the buttons. */
    const error = document.createElement('p');
    error.id = 'voucher-redeem-error';
    error.className = 'voucher-redeem-error';
    error.setAttribute('role', 'alert');
    error.setAttribute('aria-live', 'assertive');

    const actions = document.createElement('div');
    actions.className = 'voucher-redeem-actions';
    const cancel = document.createElement('button');
    cancel.type = 'button';
    cancel.className = 'btn deck-btn deck-btn-secondary voucher-redeem-cancel';
    cancel.dataset.voucherAction = 'cancel';
    cancel.textContent = uiText('common.cancel', 'Cancel');
    const submit = document.createElement('button');
    submit.type = 'button';
    submit.className = 'btn deck-btn deck-btn-primary voucher-redeem-submit';
    submit.dataset.voucherAction = 'redeem';
    submit.textContent = uiText('voucher.redeem', 'Redeem');
    actions.append(cancel, submit);

    panel.append(eyebrow, title, label, input, help, error, actions);
    overlay.appendChild(panel);
    return { overlay, panel, input, error, cancel, submit };
  }

  function open({ code = null, source = 'menu', returnFocus = null } = {}) {
    if (isOpen()) return false;
    if (!document.body) return false;
    if (document.body.classList.contains('game-active') || document.body.classList.contains('game-transition-pending')) return false;
    if (anotherModalOwnsInput()) return false;

    const { overlay, input, error, cancel, submit } = build({ code });
    const previousFocus = returnFocus instanceof HTMLElement
      ? returnFocus
      : (document.activeElement instanceof HTMLElement ? document.activeElement : null);
    /* The store lives OUTSIDE #app, so both are isolated and both restored. */
    const isolated = ['app', 'store-modal']
      .map((id) => document.getElementById(id))
      .filter(Boolean)
      .map((element) => ({ element, wasInert: Boolean(element.inert) }));
    isolated.forEach(({ element }) => { element.inert = true; });

    let closed = false;
    let pending = false;
    let generation = 0;

    /* THE DIALOG FOLLOWS THE KEYBOARD (owner, iOS Brave, landscape): "the
       keyboard is rendered on top of it ... usually when typing you are able
       to see the text you type and move the cursor".

       iOS does not shrink the layout viewport when the keyboard opens — it
       shrinks the VISUAL viewport and slides it. A `position: fixed; inset: 0`
       overlay keeps sizing itself to the layout viewport, so its centred panel
       kept its centre under the keys: on a 932x300 page box the keyboard
       leaves ~110-170px visible, the panel's input sat at y≈150 and the
       Cancel/Redeem row under the glass. The page cannot scroll a fixed box
       into view for you.

       So while a keyboard is up the overlay is sized and placed to the visual
       viewport itself, and the panel drops to its compact one-row layout
       (`data-keyboard="open"` in voucher-redeem.css: input, Cancel, Redeem,
       error line) which fits in ~90px. The threshold is a keyboard, not a
       browser bar: bars retracting make the visual viewport GROW, and a URL
       bar is under 100px on every profile in mobileViewports.js. Android
       Chrome resizes the layout viewport instead, which the same stylesheet
       handles with a max-height band — one layout, two triggers. */
    const visualViewport = window.visualViewport || null;
    const KEYBOARD_MIN_SHRINK_PX = 100;
    function fitToVisualViewport() {
      if (!visualViewport || !overlay.isConnected) return;
      const shrunk = visualViewport.height < window.innerHeight - KEYBOARD_MIN_SHRINK_PX;
      if (shrunk) {
        overlay.style.top = `${Math.max(0, visualViewport.offsetTop)}px`;
        overlay.style.left = `${Math.max(0, visualViewport.offsetLeft)}px`;
        overlay.style.width = `${visualViewport.width}px`;
        overlay.style.height = `${visualViewport.height}px`;
        overlay.dataset.keyboard = 'open';
      } else {
        overlay.style.top = '';
        overlay.style.left = '';
        overlay.style.width = '';
        overlay.style.height = '';
        delete overlay.dataset.keyboard;
      }
    }
    visualViewport?.addEventListener?.('resize', fitToVisualViewport);
    visualViewport?.addEventListener?.('scroll', fitToVisualViewport);

    function close({ restoreFocus = true } = {}) {
      if (closed) return false;
      closed = true;
      generation += 1;
      document.removeEventListener('keydown', onKeyDown, true);
      visualViewport?.removeEventListener?.('resize', fitToVisualViewport);
      visualViewport?.removeEventListener?.('scroll', fitToVisualViewport);
      overlay.remove();
      isolated.forEach(({ element, wasInert }) => { element.inert = wasInert; });
      if (restoreFocus && previousFocus?.isConnected) {
        try { previousFocus.focus({ preventScroll: true }); } catch (_) { previousFocus.focus(); }
      }
      ArrivalCeremony.release();
      return true;
    }

    function setPending(flag) {
      pending = flag;
      submit.disabled = flag;
      submit.classList.toggle('btn-pending', flag);
      submit.setAttribute('aria-busy', flag ? 'true' : 'false');
      submit.textContent = flag ? uiText('voucher.redeeming', 'Redeeming…') : uiText('voucher.redeem', 'Redeem');
      input.readOnly = flag;
    }

    async function submitCode() {
      if (pending || closed) return;
      const raw = input.value;
      if (!isPlausibleVoucherCode(raw)) {
        error.textContent = uiText('voucher.error.shape', 'Enter the code exactly as it was given to you.');
        input.focus();
        input.select();
        return;
      }
      error.textContent = '';
      setPending(true);
      const myGeneration = generation;
      const outcome = await redeemCode(raw, { source, shouldContinue: () => !closed && myGeneration === generation,
        beforeReward: () => { close({ restoreFocus: false }); } });
      if (closed || myGeneration !== generation) return;
      if (outcome.ok === true) {
        close({ restoreFocus: false });
      } else {
        setPending(false);
        error.textContent = outcome.message;
        input.focus();
        input.select();
      }
    }

    function onKeyDown(event) {
      /* A listener whose dialog is gone (torn down by something other than
         close) must not keep eating the keyboard. */
      if (!overlay.isConnected) {
        document.removeEventListener('keydown', onKeyDown, true);
        return;
      }
      // The gift review owns input while it is above this code dialog.
      if (document.body.classList.contains('mf-confirm-open')) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopImmediatePropagation();
        close();
        return;
      }
      if (event.key === 'Tab') {
        const ring = [input, cancel, submit].filter((element) => !element.disabled);
        const index = ring.indexOf(/** @type {any} */ (document.activeElement));
        const next = event.shiftKey
          ? (index <= 0 ? ring[ring.length - 1] : ring[index - 1])
          : (index < 0 || index === ring.length - 1 ? ring[0] : ring[index + 1]);
        event.preventDefault();
        event.stopImmediatePropagation();
        next.focus();
      }
    }

    input.addEventListener('input', () => {
      const formatted = formatVoucherCodeForDisplay(input.value);
      if (formatted !== input.value) {
        input.value = formatted;
        try { input.setSelectionRange(formatted.length, formatted.length); } catch (_) { /* not every input type */ }
      }
      if (error.textContent) error.textContent = '';
    });
    input.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        void submitCode();
      }
    });
    submit.addEventListener('click', () => { void submitCode(); });
    cancel.addEventListener('click', () => close());
    overlay.addEventListener('click', (event) => {
      if (event.target === overlay) close();
    });
    document.addEventListener('keydown', onKeyDown, true);

    document.body.appendChild(overlay);
    ArrivalCeremony.hold();
    /* A keyboard may already be up (the dialog can open from a focused
       field's flow); size to what is visible now, not to the next event. */
    fitToVisualViewport();
    input.focus();
    if (input.value) input.select();
    return true;
  }

  function close() {
    const overlay = document.getElementById(OVERLAY_ID);
    if (!overlay) return false;
    const cancel = /** @type {HTMLElement|null} */ (overlay.querySelector('[data-voucher-action="cancel"]'));
    if (cancel) cancel.click();
    return true;
  }

  return { open, close, isOpen, redeemCode };
})();

export { VoucherRedeem };
